(() => {
  if (customElements.get('nfd-product-discovery')) return;

  class NfdProductDiscovery extends HTMLElement {
    static get observedAttributes() { return ['data-product-id']; }
    attributeChangedCallback(name, previous, current) {
      if (!previous || previous === current || !this.isConnected) return;
      this.disconnectedCallback(); this.loaded = false;
      queueMicrotask(() => { if (this.isConnected) this.connectedCallback(); });
    }
    connectedCallback() {
      this.preservePreviewLinks();
      if (this.loaded || this.pending || this.observer) return;
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.observer = null;
        this.load();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      if (this.pending) {
        this.controller?.abort();
        this.controller = null;
        this.pending = false;
      }
    }

    async fetchRow(intent, signal) {
      const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid recommendations origin');
      url.search = new URLSearchParams({
        product_id: this.dataset.productId,
        section_id: this.dataset.sectionId,
        limit: '10',
        intent
      });
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { signal, priority: 'low' });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      return document.querySelector(`[data-discovery-row="${intent}"]`);
    }

    async load() {
      if (this.loaded || this.pending) return;
      this.pending = true;
      const controller = new AbortController();
      this.controller = controller;
      const intents = this.dataset.hasFit === 'true' ? ['complementary'] : ['complementary', 'related'];
      const results = await Promise.allSettled(intents.map(intent => this.fetchRow(intent, controller.signal)));
      if (this.controller !== controller || !this.isConnected) return;
      this.pending = false;
      this.loaded = true;
      const seen = new Set([this.dataset.productId]);
      for (let index = 0; index < intents.length; index++) {
        const result = results[index];
        const slot = this.querySelector(`[data-discovery-slot="${intents[index]}"]`);
        if (result.status === 'fulfilled' && result.value) slot.replaceChildren(document.importNode(result.value, true));
      }
      for (const row of this.querySelectorAll('[data-discovery-row]')) {
        const limit = row.dataset.discoveryRow === 'complementary' ? 3 : 4;
        let count = 0;
        for (const card of row.querySelectorAll('[data-discovery-product]')) {
          const id = card.dataset.discoveryProduct;
          if (seen.has(id) || count >= limit) {
            card.remove();
            continue;
          }
          seen.add(id);
          count++;
        }
        if (count === 0) row.remove();
        else row.querySelector('.nfd-discovery__products').dataset.count = String(count);
      }
      this.preservePreviewLinks();
      this.dataset.loaded = 'true';
    }

    preservePreviewLinks() {
      const current = new URL(window.location.href);
      if (current.searchParams.get('view') === 'nyc-studio') this.querySelectorAll('a[href]').forEach(link => {
        const url = new URL(link.getAttribute('href'), current);
        if (url.origin !== current.origin || !url.pathname.includes('/products/')) return;
        url.searchParams.set('view', 'nyc-studio');
        if (current.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', current.searchParams.get('preview_theme_id'));
        link.href = url.pathname + url.search;
      });
    }
  }

  customElements.define('nfd-product-discovery', NfdProductDiscovery);
})();
